// WCAG-2.1-Kontrastmathematik für das Janeway-Theme-System.
//
// Diese Datei ist die EINZIGE Quelle für Kontrastregeln. Der CI-Test
// (`__tests__/theme-contrast.test.ts`) und die Live-Warnung im Design-Tab
// importieren beide von hier — was die Pipeline durchsetzt, ist damit exakt das,
// was der Nutzer beim Einstellen angezeigt bekommt. Zwei Kopien wären zwei
// Wahrheiten, und die auseinanderlaufende wäre immer die, die der Nutzer sieht.

import type { ThemeVars } from "./themes";

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function parseHsl(value: string): Hsl | null {
  const m = value?.match(/hsl\(\s*([\d.]+)\s*,?\s*([\d.]+)%\s*,?\s*([\d.]+)%/i);
  return m ? { h: +m[1], s: +m[2], l: +m[3] } : null;
}

export function formatHsl({ h, s, l }: Hsl): string {
  return `hsl(${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%)`;
}

function toRgb({ h, s, l }: Hsl): [number, number, number] {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  const m = ln - c / 2;
  return [rgb[0] + m, rgb[1] + m, rgb[2] + m];
}

/** Gegenstueck zu `toRgb` — Kanaele 0..1, gammabehaftetes sRGB. */
function toHsl([r, g, b]: [number, number, number]): Hsl {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l: l * 100 };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = 60 * (((g - b) / d) % 6);
  else if (max === g) h = 60 * ((b - r) / d + 2);
  else h = 60 * ((r - g) / d + 4);
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100 };
}

/**
 * Zwei Farben in sRGB mischen — dasselbe Ergebnis wie CSS
 * `color-mix(in srgb, <a> <anteilA>%, <b>)`.
 *
 * WARUM NICHT IN HSL INTERPOLIEREN: HSL ist kein linearer Raum. Zwischen einem
 * dunklen und einem hellen Ton derselben Familie laeuft eine HSL-Mischung ueber
 * eine andere Helligkeitskurve als die, die der Browser malt — der berechnete
 * Wert saehe neben dem gemalten sichtbar anders aus. Und genau darum geht es
 * hier: dieselbe Zahl in TypeScript wie in `globals.css`.
 *
 * `toRgb` liefert gammabehaftete Kanaele (die gewoehnliche HSL→RGB-Umrechnung),
 * und `color-mix(in srgb, …)` rechnet ebenfalls gammabehaftet — die geradlinige
 * Mischung trifft es also. Der Rueckweg ueber `formatHsl` rundet auf ganze
 * Prozent; der Unterschied liegt unter einem 255stel und ist nicht sichtbar.
 */
export function mischen(a: string, b: string, anteilA: number): string | null {
  const ha = parseHsl(a);
  const hb = parseHsl(b);
  if (!ha || !hb) return null;
  const ra = toRgb(ha);
  const rb = toRgb(hb);
  const t = Math.min(100, Math.max(0, anteilA)) / 100;
  return formatHsl(
    toHsl([
      ra[0] * t + rb[0] * (1 - t),
      ra[1] * t + rb[1] * (1 - t),
      ra[2] * t + rb[2] * (1 - t),
    ]),
  );
}

const linearize = (v: number) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);

function relLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map(linearize);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Kontrastverhältnis zweier HSL-Farbstrings. `null`, wenn eine nicht lesbar ist. */
export function contrastRatio(a: string, b: string): number | null {
  const ha = parseHsl(a);
  const hb = parseHsl(b);
  if (!ha || !hb) return null;
  const la = relLuminance(toRgb(ha));
  const lb = relLuminance(toRgb(hb));
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Kontrast von Text auf einer TEILDURCHSICHTIGEN Fläche über einem Untergrund.
 * Nötig für `hover:bg-primary/90` am Button: der Ruhezustand kann die Schwelle
 * erfüllen und der Hover-Zustand trotzdem darunter fallen.
 */
export function contrastOverBlend(
  text: string,
  layer: string,
  under: string,
  alpha: number,
): number | null {
  const ht = parseHsl(text);
  const hl = parseHsl(layer);
  const hu = parseHsl(under);
  if (!ht || !hl || !hu) return null;
  const l = toRgb(hl);
  const u = toRgb(hu);
  const blended = l.map((v, i) => v * alpha + u[i] * (1 - alpha)) as [number, number, number];
  const lb = relLuminance(blended);
  const lt = relLuminance(toRgb(ht));
  return (Math.max(lt, lb) + 0.05) / (Math.min(lt, lb) + 0.05);
}

export type TokenKey = keyof ThemeVars;

export interface ContrastPair {
  /** Token, das im Vordergrund steht (Text bzw. Linie). */
  fg: TokenKey;
  /** Token, gegen das geprüft wird. */
  bg: TokenKey;
  /** WCAG-Mindestwert: 4.5 für Fließtext (1.4.3), 3.0 für Bedienelemente (1.4.11). */
  min: number;
  /** Klartext für die Anzeige — kein Token-Kauderwelsch im Nutzerdialog. */
  label: string;
  /** Welches der beiden Token bei einem Verstoß sinnvollerweise weicht. */
  adjust: TokenKey;
  /** Gruppe im Design-Tab. */
  group: string;
}

// Hier stand: „`border` fehlt bewusst: ein Karten- oder Trennrahmen ist dekorativ
// und fällt nicht unter 1.4.11." Der Halbsatz stimmt und bleibt gültig — deshalb
// steht `border` unten mit **1,5** und nicht mit den 3,0 von `input`/`ring`, die
// Bedienelemente bzw. den Fokus kennzeichnen und pflichtig sind.
//
// Was daraus fälschlich folgte, war „also gar keine Schwelle". Zwischen
// „dekorativ" und „unsichtbar" liegt der ganze Unterschied: der rahmenlose Aufbau
// trägt seine Gliederung ALLEIN über diese Linie. Sie erreichte 1,5 : 1 in 30 von
// 516 Fällen (43 Themes × hell/dunkel × 6 Flächen) — die Suite stand auf den
// meisten Themes ohne sichtbare Struktur da, und niemand bekam es gesagt.
export const CONTRAST_PAIRS: ContrastPair[] = [
  { fg: "foreground", bg: "background", min: 4.5, label: "Fließtext auf der Seite", adjust: "foreground", group: "Seite" },
  { fg: "muted-foreground", bg: "background", min: 4.5, label: "Hilfetext auf der Seite", adjust: "muted-foreground", group: "Seite" },
  { fg: "card-foreground", bg: "card", min: 4.5, label: "Text auf Karten", adjust: "card-foreground", group: "Karte" },
  { fg: "muted-foreground", bg: "card", min: 4.5, label: "Hilfetext auf Karten", adjust: "muted-foreground", group: "Karte" },
  { fg: "primary-foreground", bg: "primary", min: 4.5, label: "Button-Beschriftung", adjust: "primary-foreground", group: "Primär" },
  { fg: "primary", bg: "background", min: 3.0, label: "Buttonfläche gegen die Seite", adjust: "primary", group: "Primär" },
  { fg: "primary", bg: "card", min: 3.0, label: "Buttonfläche gegen Karten", adjust: "primary", group: "Primär" },
  { fg: "secondary-foreground", bg: "secondary", min: 4.5, label: "Text auf Sekundärflächen", adjust: "secondary-foreground", group: "Sekundär" },
  { fg: "muted-foreground", bg: "muted", min: 4.5, label: "Hilfetext auf gedämpfter Fläche", adjust: "muted-foreground", group: "Gedämpft" },
  { fg: "accent-foreground", bg: "accent", min: 4.5, label: "Text beim Überfahren", adjust: "accent-foreground", group: "Akzent" },
  { fg: "destructive-foreground", bg: "destructive", min: 4.5, label: "Text auf Gefahrenflächen", adjust: "destructive-foreground", group: "Gefahr" },
  // Die zweite Luecke desselben Musters: `destructive` selbst stand hier nie als TEXT,
  // nur als Flaeche (Zeile oben). Gemessen ueber alle 43 Themes dunkel: 41 fielen als
  // Fehlertext auf card/background/muted unter 4,5:1 (2,47:1 bis 4,06:1) — der Waechter
  // sah nur die halbe Verwendung. `accent` bekommt bewusst 3,0 statt 4,5: die einzige
  // reale Stelle ist ein Icon-only-Hover (`KeyManager.tsx`), also 1.4.11, nicht 1.4.3.
  { fg: "destructive", bg: "background", min: 4.5, label: "Fehlertext auf der Seite", adjust: "destructive", group: "Gefahr" },
  { fg: "destructive", bg: "card", min: 4.5, label: "Fehlertext auf Karten", adjust: "destructive", group: "Gefahr" },
  { fg: "destructive", bg: "muted", min: 4.5, label: "Fehlertext auf gedämpfter Fläche", adjust: "destructive", group: "Gefahr" },
  { fg: "destructive", bg: "accent", min: 3.0, label: "Fehler-Symbol beim Überfahren", adjust: "destructive", group: "Gefahr" },
  { fg: "input", bg: "background", min: 3.0, label: "Rahmen von Eingabefeldern", adjust: "input", group: "Linien & Felder" },
  // Lücke im Muster: `ring` und `primary` werden gegen BEIDE Flächen geprüft,
  // `input` bisher nur gegen `background`. Formulare stehen im Ökosystem in
  // `Card` (`cardCls` = "… bg-card …"), also auf `card` — und dort lag der
  // Feldrahmen in allen 43 dunklen Modi unter 3,0 (nord 2,56 · zinc 2,96).
  { fg: "input", bg: "card", min: 3.0, label: "Rahmen von Eingabefeldern auf Karten", adjust: "input", group: "Linien & Felder" },
  { fg: "ring", bg: "background", min: 3.0, label: "Fokusring auf der Seite", adjust: "ring", group: "Linien & Felder" },
  { fg: "ring", bg: "card", min: 3.0, label: "Fokusring auf Karten", adjust: "ring", group: "Linien & Felder" },
  // DIE LUECKE, DIE ALLES ERKLAERT: `border` stand hier bis 0.256.0 NICHT drin.
  // Der Feldrahmen wurde geprüft, der Fokusring wurde geprüft — die Trennlinie,
  // die in jeder App an jeder Kante sitzt, nicht. Sie trug 1,5 : 1 in 30 von 516
  // Fällen (43 Themes × hell/dunkel × 6 Flächen), und niemand bekam es gesagt.
  // Statt 1876 Fundstellen umzuschreiben, hebt `themes.ts` jetzt den Token selbst;
  // diese beiden Zeilen sind der Wächter, der das festhält — und die Live-Warnung
  // im Design-Tab, sobald jemand ein Theme mit unsichtbarer Kante anlegt.
  // 1,5 statt 3,0: Begründung an `KANTE_MIN` in `themes.ts`.
  { fg: "border", bg: "background", min: 1.5, label: "Trennlinie auf der Seite", adjust: "border", group: "Linien & Felder" },
  { fg: "border", bg: "card", min: 1.5, label: "Trennlinie auf Karten", adjust: "border", group: "Linien & Felder" },
  { fg: "sidebar-foreground", bg: "sidebar", min: 4.5, label: "Text in der Seitenleiste", adjust: "sidebar-foreground", group: "Seitenleiste" },
  { fg: "sidebar-primary-foreground", bg: "sidebar-primary", min: 4.5, label: "Text auf dem aktiven Eintrag", adjust: "sidebar-primary-foreground", group: "Seitenleiste" },
  { fg: "sidebar-accent-foreground", bg: "sidebar-accent", min: 4.5, label: "Text beim Überfahren (Seitenleiste)", adjust: "sidebar-accent-foreground", group: "Seitenleiste" },
  { fg: "sidebar-primary", bg: "sidebar", min: 3.0, label: "Aktiver Eintrag gegen die Seitenleiste", adjust: "sidebar-primary", group: "Seitenleiste" },
];

export type ContrastRating = "fehler" | "knapp" | "gut";

/**
 * Drei Stufen statt zwei. „Knapp" ist nicht willkürlich: `Button` trägt
 * `hover:bg-primary/90`, und genau diese Reserve frisst der Hover-Zustand.
 * Ein Wert exakt auf der Schwelle fällt beim Überfahren wieder darunter.
 */
export function rate(ratio: number, min: number): ContrastRating {
  if (ratio < min) return "fehler";
  if (ratio < min * 1.1) return "knapp";
  return "gut";
}

export interface ContrastFinding extends ContrastPair {
  ratio: number;
  rating: ContrastRating;
  fgValue: string;
  bgValue: string;
}

// Bewusst `string | undefined`: der Design-Tab legt die Anpassungen des Nutzers
// über die Theme-Grundwerte, und dieser Satz ist naturgemäß lückenhaft. Ein
// fehlender Wert ist kein Fehler — er wird übersprungen, statt den Aufrufer zu
// zwingen, vorher zu filtern.
type VarBag = Record<string, string | undefined>;

/** Prüft einen kompletten Variablensatz gegen alle kuratierten Paare. */
export function checkVars(vars: VarBag): ContrastFinding[] {
  const out: ContrastFinding[] = [];
  for (const p of CONTRAST_PAIRS) {
    const fgValue = vars[p.fg];
    const bgValue = vars[p.bg];
    if (!fgValue || !bgValue) continue;
    const ratio = contrastRatio(fgValue, bgValue);
    if (ratio === null) continue;
    out.push({ ...p, ratio, rating: rate(ratio, p.min), fgValue, bgValue });
  }
  return out;
}

/**
 * Der EINE Befund, der ein Theme am ehesten unbrauchbar macht.
 *
 * Sortiert nach `ratio / min`, nicht nach `ratio`: 3,1 : 1 bei einem Mindestwert
 * von 3,0 ist in Ordnung, 4,4 : 1 bei 4,5 nicht — die rohe Zahl allein würde die
 * beiden verwechseln. Damit lässt sich ein Theme mit einem Satz beschreiben,
 * ohne dass der Nutzer eine Liste lesen muss.
 */
export function schwaechsterBefund(vars: VarBag): ContrastFinding | null {
  let schlechtester: ContrastFinding | null = null;
  for (const f of checkVars(vars)) {
    if (!schlechtester || f.ratio / f.min < schlechtester.ratio / schlechtester.min) {
      schlechtester = f;
    }
  }
  return schlechtester;
}

/** Zusätzliche Prüfung des Hover-Zustands am Standard-Button. */
export function checkHover(vars: VarBag): { ratio: number; rating: ContrastRating } | null {
  const fg = vars["primary-foreground"];
  const bg = vars.primary;
  const under = vars.background;
  if (!fg || !bg || !under) return null;
  const r = contrastOverBlend(fg, bg, under, 0.9);
  if (r === null) return null;
  return { ratio: r, rating: rate(r, 4.5) };
}

/**
 * Dieselbe Prüfung für den destruktiven Button/Badge (`hover:bg-destructive/90`,
 * `Button.tsx`) — gegen BEIDE Untergründe, auf denen er vorkommt: die Seite
 * (`background`) und eine Karte (`card`, z.B. `DangerZone.tsx`). Anders als
 * `checkHover` liefert sie eine Liste statt eines Werts, weil beide Fälle real sind
 * und keiner den anderen ersetzt.
 */
export function checkDestructiveHover(
  vars: VarBag,
): Array<{ unter: "background" | "card"; ratio: number; rating: ContrastRating }> | null {
  const fg = vars["destructive-foreground"];
  const bg = vars.destructive;
  if (!fg || !bg) return null;
  const ergebnisse: Array<{ unter: "background" | "card"; ratio: number; rating: ContrastRating }> = [];
  for (const unter of ["background", "card"] as const) {
    const under = vars[unter];
    if (!under) continue;
    const r = contrastOverBlend(fg, bg, under, 0.9);
    if (r !== null) ergebnisse.push({ unter, ratio: r, rating: rate(r, 4.5) });
  }
  return ergebnisse.length ? ergebnisse : null;
}

/**
 * Sucht die KLEINSTE Helligkeitsverschiebung von `adjust`, die gegen `against`
 * den Zielwert erreicht. Farbton und Sättigung bleiben unangetastet — der
 * Charakter der Farbe soll erhalten bleiben, sie soll nur lesbar werden.
 *
 * Liefert `null`, wenn das Ziel per Helligkeit allein nicht erreichbar ist
 * (kommt vor, wenn beide Token nah beieinander liegen und die Fläche zugleich
 * gegen einen dritten Wert bestehen muss).
 */
export function suggestFix(
  adjust: string | undefined,
  against: string | undefined,
  target: number,
): string | null {
  if (!adjust || !against) return null;
  const base = parseHsl(adjust);
  if (!base || !parseHsl(against)) return null;
  for (let d = 1; d <= 100; d++) {
    for (const dir of [-1, 1]) {
      const l = base.l + dir * d;
      if (l < 0 || l > 100) continue;
      const candidate = { ...base, l };
      const r = contrastRatio(formatHsl(candidate), against);
      // Etwas über die Schwelle zielen, damit der Hover-Zustand nicht sofort
      // wieder darunter fällt (siehe `rate`).
      if (r !== null && r >= target * 1.02) return formatHsl(candidate);
    }
  }
  return null;
}
